/**
 * Guide · Scenarios — the unified /scenarios screen. Merges the former SKU
 * velocity field and Capacity scenarios guide topics: every SKU held at the
 * UKDC plotted by velocity, stacked live against the site's pick-face
 * ceiling through 2029, with five levers to change what's held where.
 */

import { GuidePage } from "@/features/guide/guide-page";

export function GuideScenariosPage() {
  return (
    <GuidePage
      eyebrow="Scenarios"
      title="One screen: the whole field, and the ceiling it has to fit under."
      intro="Scenarios is the joint Supply / Commercial / Planning view: every SKU held at the UKDC plotted by how fast it moves against the money or space it consumes, stacked live against the site's pick-face ceiling through 2029. Pull a lever — relocate, pack rationalise, Rx→Dx convert, add capacity, or pace the NPI rollout — and watch the capacity allocation, the pick faces, and the velocity scatter all react to the same draft scenario at once."
      liveBullets={[
        {
          text: "Center: capacity allocation stacks the held total against the pick-face ceiling year by year to 2029, pick faces show exactly which SKUs occupy the freed or added space, and the velocity scatter plots every held SKU by how fast it moves against the money or space it consumes",
        },
        {
          text: "Five levers on the right — relocate non-movers, pack rationalise, Rx→Dx convert, add pick-face capacity, and pace the NPI intake — each with its capital, lead-time and space trade-off, and every edit updates the centre live",
        },
        {
          text: "Save a scenario and it joins the four-view rail on the left, so you can compare saved cases side by side without losing the live draft",
        },
        {
          text: "Ask the agent in plain language, or click Agent Resolve, to have it set the levers for you and read back what changed",
        },
      ]}
      walkthrough={[
        {
          step: "Read the field and the ceiling",
          body: "Start from the velocity scatter and the capacity allocation together — see which SKUs are costing the most space or money, and how close the held total already sits to the ceiling.",
        },
        {
          step: "Pull a lever",
          body: "Try relocation, pack rationalisation, Rx→Dx conversion, added capacity, or NPI pacing and watch the centre — allocation, pick faces and scatter — react to the draft in real time.",
        },
        {
          step: "Save, compare, and ask the agent",
          body: "Save a scenario to send it to the four-view rail for comparison, or hand the levers to the agent — ask it directly, or click Agent Resolve, and it sets them for you.",
        },
      ]}
      cta={{ to: "/scenarios", label: "Open Scenarios" }}
    />
  );
}
